import React, { useEffect, useRef, useState } from "react";
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import {
  StyledTopicsHeader,
  StyledTopicSideBarInfoBox,
} from "../../../common/StyledComponents/SideBarComponents";
import {
  CustomInput,
  SearchBox,
  ChatSearchButton,
  SmallIcon,
} from "../../../common/chatComponents/ScreenSearchSection";
import searchIcon from "../../../assets/images/chats/gallery_active.svg";
import starredIcon from "../../../assets/images/chats/starred_icon.svg";
import unstarIcon from "../../../assets/images/chats/unstar_icon.svg";
import {
  handleFetchGroupThreads,
  handleToggleThreadStar,
} from "../../../appStore/actions/ArkchatGroup/action";
import {
  handleThreadListPanel,
  handleThreadToOpenFromList,
  handleAppSelectedThread,
} from "../../../appStore/actions/AppState/action";
import { crossIcon, crossIconDiv } from "../../../assets/commonFunction";
import useAppSelectors from "../../../customHooks/useAppSelectors";

const PAGE_SIZE = 50;

const classes = {
  headingText: {
    color: "black",
    fontSize: "14px",
    fontWeight: "400",
    "@media (max-width: 63em)": {
      fontSize: "0.8rem",
    },
  },
  iconDiv: crossIconDiv,
  iconn: crossIcon,
  threadItem: {
    cursor: "pointer",
    borderRadius: "0.1rem",
    padding: "0.3rem 0.5rem",
    "&:hover": {
      backgroundColor: "#E1E1E1",
    },
  },
  starFilterBtn: (active) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "30px",
    height: "30px",
    borderRadius: "0.15rem",
    border: active ? "0.5px solid #183243" : "0.5px solid #000000",
    backgroundColor: active ? "#183243" : "#ffffff",
    cursor: "pointer",
  }),
  searchIconBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "30px",
    height: "30px",
    borderRadius: "0.15rem",
    border: "none",
    backgroundColor: "transparent",
    cursor: "pointer",
  },
};

const Threads = ({ isCollapsed, setCollapsed }) => {
  const dispatch = useDispatch();
  const { selectedGroup, currentHomeIndexTab } = useAppSelectors();
  const selectedTopic = useSelector(
    (state) => state?.appStateData?.selectedTopicData,
  );
  const selectedSubtopic = useSelector(
    (state) => state?.appStateData?.selectedSubtopicData,
  );
  const selectedThreadData = useSelector(
    (state) => state?.appStateData?.selectedThreadData,
  );

  const [threads, setThreads] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchThreadQuery, setSearchThreadQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [showStarredOnly, setShowStarredOnly] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const loadingRef = useRef(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchThreadQuery.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchThreadQuery]);

  const fetchThreads = async (pageToLoad, searchTerm, replace = false) => {
    if (!selectedGroup?.id || loadingRef.current) return;
    loadingRef.current = true;
    setIsLoading(true);
    const res = await dispatch(
      handleFetchGroupThreads(
        selectedGroup?.id,
        pageToLoad,
        PAGE_SIZE,
        searchTerm,
        selectedTopic?.id || "",
        selectedSubtopic?.id || "",
        showStarredOnly,
      ),
    );
    const newItems = res?.data?.messages || [];
    const count = res?.data?.count || 0;
    setThreads((prev) => (replace ? newItems : [...prev, ...newItems]));
    setHasMore(pageToLoad * PAGE_SIZE < count);
    setPage(pageToLoad);
    setIsLoading(false);
    loadingRef.current = false;
  };

  useEffect(() => {
    setThreads([]);
    fetchThreads(1, debouncedSearch, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroup?.id, debouncedSearch, selectedTopic?.id, selectedSubtopic?.id, showStarredOnly]);

  const handleListScroll = (event) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (hasMore && !loadingRef.current && scrollHeight - scrollTop - clientHeight < 100) {
      fetchThreads(page + 1, debouncedSearch);
    }
  };

  const handleThreadClick = (item) => {
    if (currentHomeIndexTab === 0) {
      dispatch(handleThreadToOpenFromList(item));
      return;
    }
    // Track Work/Performance: a thread filters the results instead of
    // opening the chat thread drawer. Clicking the active thread again
    // clears the filter.
    dispatch(
      handleAppSelectedThread(selectedThreadData?.id === item?.id ? null : item),
    );
  };

  const handleStarClick = async (event, item) => {
    event.stopPropagation();
    const wasStarred = item?.isThreadStarred;
    // Optimistic update; reverted below if the API call fails
    setThreads((prev) =>
      showStarredOnly && wasStarred
        ? prev.filter((t) => t?.id !== item?.id)
        : prev.map((t) =>
          t?.id === item?.id ? { ...t, isThreadStarred: !wasStarred } : t,
        ),
    );
    const res = await dispatch(handleToggleThreadStar(item?.id));
    if (!res?.status) {
      setThreads((prev) =>
        showStarredOnly && wasStarred
          ? [...prev, item].sort((a, b) => Number(b?.timetoken) - Number(a?.timetoken))
          : prev.map((t) =>
            t?.id === item?.id ? { ...t, isThreadStarred: wasStarred } : t,
          ),
      );
    }
  };

  const getThreadLabel = (item) =>
    item?.thread_heading
      ? `# ${item?.thread_number} - ${item?.thread_heading}`
      : `# ${item?.thread_number ?? ""} Thread`;

  const getThreadDate = (item) => {
    const date = item?.createdAt
      ? moment(item?.createdAt)
      : moment(Math.floor(Number(item?.timetoken) / 10000));
    return date.isValid() ? date.format("DD-MM-YYYY") : "";
  };

  if (isCollapsed) {
    return (
      <Stack
        bgcolor={"#FDFDFD"}
        height={"100%"}
        alignItems={"center"}
        sx={{ cursor: "pointer", overflow: "hidden" }}
        onClick={() => setCollapsed(false)}
      >
        <Box
          width={"100%"}
          minHeight={"38px"}
          display={"flex"}
          alignItems={"center"}
          justifyContent={"center"}
        >
          <KeyboardDoubleArrowRightIcon
            sx={{ fontSize: "1.3rem", color: "#000" }}
          />
        </Box>
        <Typography
          variant="h6"
          sx={{
            ...classes.headingText,
            writingMode: "vertical-rl",
            transform: "rotate(180deg)",
            mt: "0.5rem",
            whiteSpace: "nowrap",
          }}
        >
          Threads
        </Typography>
      </Stack>
    );
  }

  return (
    <React.Fragment>
      <Stack sx={{ paddingLeft: "0", bgcolor: "#FDFDFD", height: "100%", overflow: "hidden" }}>
        <StyledTopicsHeader sx={{ bgcolor: "#FDFDFD" }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
          >
            <Box display="flex" alignItems="center" gap={"0.5rem"} flex={1} minWidth={0}>
              {/* <Typography sx={classes.iconDiv}>
                <CloseIcon
                  sx={classes.iconn}
                  onClick={() => dispatch(handleThreadListPanel(false))}
                />
              </Typography> */}
              <KeyboardDoubleArrowLeftIcon
                onClick={() => setCollapsed(true)}
                sx={{ fontSize: "1.3rem", color: "#000", cursor: "pointer", flexShrink: 0 }}
              />
              {isSearchOpen ? (
                <SearchBox sx={{ flex: 1, mr: "0.3rem", height: "1.87rem", boxSizing: "border-box" }}>
                  <CustomInput
                    autoFocus
                    value={searchThreadQuery}
                    onChange={(e) => setSearchThreadQuery(e.target.value)}
                    placeholder="Search Threads"
                  />
                  <ChatSearchButton
                    size="small"
                    variant="contained"
                    onClick={() => { setIsSearchOpen(false); setSearchThreadQuery(""); }}
                  >
                    <CloseIcon sx={{ fontSize: "1rem", color: "#000" }} />
                  </ChatSearchButton>
                </SearchBox>
              ) : (
                <Box>
                  <Typography sx={classes.headingText} variant="h6">
                    Threads
                  </Typography>
                </Box>
              )}
            </Box>
            <Box display="flex" alignItems="center" gap={"0.3rem"} flexShrink={0}>
              {!isSearchOpen && (
                <Box
                  sx={classes.searchIconBtn}
                  title="Search threads"
                  onClick={() => setIsSearchOpen(true)}
                >
                  <SmallIcon src={searchIcon} />
                </Box>
              )}
              <Box
                sx={classes.starFilterBtn(showStarredOnly)}
                title={showStarredOnly ? "Show all threads" : "Show starred threads"}
                onClick={() => setShowStarredOnly((prev) => !prev)}
              >
                <img
                  src={showStarredOnly ? starredIcon : unstarIcon}
                  alt="starred filter"
                  style={{
                    width: "16px",
                    height: "16px",
                    filter: showStarredOnly ? "brightness(0) invert(1)" : "none",
                  }}
                />
              </Box>
            </Box>
          </Box>
        </StyledTopicsHeader>

        <StyledTopicSideBarInfoBox
          sx={{
            bgcolor: "#FDFDFD",
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            padding: "0.3rem 0.3rem 0",
          }}
        >
          {/* <Box position={"relative"} width={"100%"} padding={"0.2rem 0.5rem"} borderRadius="0.1rem" title={"View Topic wise Chats"}>
            <Typography mt={"0.5rem"} fontSize={"0.84rem"} fontWeight={600}>View Thread wise Chats</Typography>
            <Typography fontSize={"0.62rem"} fontWeight={100}>Open a thread to rename it, so it's easy to spot here.</Typography>
          </Box> */}

          <Box
            onScroll={handleListScroll}
            flex={1}
            minHeight={0}
            mt={"0.4rem"}
            overflow="scroll"
            sx={{
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { width: "0px" },
            }}
          >
            {threads?.map((item) => (
              <Box
                key={`${item?.id}threaditem`}
                display="flex"
                alignItems="flex-start"
                mt={"0.5rem"}
                onClick={() => handleThreadClick(item)}
                sx={{
                  ...classes.threadItem,
                  backgroundColor:
                    currentHomeIndexTab !== 0 && selectedThreadData?.id === item?.id
                      ? "#E1E1E1"
                      : "transparent",
                }}
              >
                <Box flex={1} minWidth={0}>
                  <Typography
                    fontSize="0.85rem"
                    fontWeight="500"
                    color="black"
                    variant="h6"
                    title={getThreadLabel(item)}
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {getThreadLabel(item)}
                  </Typography>
                  <Typography fontSize="0.72rem" fontWeight="400" color="#888">
                    {[item?.from?.name, getThreadDate(item)].filter(Boolean).join(", ")}
                  </Typography>
                </Box>
                <Box
                  flexShrink={0}
                  ml={"0.3rem"}
                  onClick={(event) => handleStarClick(event, item)}
                  title={item?.isThreadStarred ? "Unstar thread" : "Star thread"}
                  sx={{ cursor: "pointer", padding: "0.2rem" }}
                >
                  <img
                    src={item?.isThreadStarred ? starredIcon : unstarIcon}
                    alt="star"
                    style={{ width: "16px", height: "16px" }}
                  />
                </Box>
              </Box>
            ))}

            {isLoading && (
              <Box display="flex" justifyContent="center" mt={"0.8rem"}>
                <CircularProgress size={20} sx={{ color: "#183243" }} />
              </Box>
            )}

            {!isLoading && threads?.length === 0 && (
              <Box textAlign="center" mt={"1rem"}>
                <Typography fontSize="0.8rem" color="#888">
                  No threads found
                </Typography>
              </Box>
            )}
          </Box>
        </StyledTopicSideBarInfoBox>
      </Stack>
    </React.Fragment>
  );
};

export default Threads;
